import { z } from "zod";

/**
 * Why a workflow step cannot complete for one patient.
 *
 * Every code here names something the practice can act on. A step that is merely *not done yet* is
 * not blocked — it is outstanding, which the run state already says. These are the cases where the
 * patient can do nothing and no amount of waiting helps.
 */
export const WORKFLOW_BLOCKER_CODE = {
  /** The lab refused the order. `detail` carries Junction's own message. */
  LabOrderRejected: "lab-order-rejected",
  /** The patient's primary provider cannot authorize an order in this practice. */
  NoAuthorizingClinician: "no-authorizing-clinician",
  /** The configured panel is missing, retired, or its markers cannot be resolved. */
  PanelUnavailable: "panel-unavailable",
  /** No visit the step offers is bookable by this patient's care team. */
  NoBookableVisit: "no-bookable-visit",
} as const;

export type WorkflowBlockerCode =
  (typeof WORKFLOW_BLOCKER_CODE)[keyof typeof WORKFLOW_BLOCKER_CODE];

const workflowStepBlockerSchema = z.object({
  /** The step this blocks. A derived sub-step carries its own generated id, same as everywhere. */
  stepId: z.string().min(1),
  code: z.nativeEnum(WORKFLOW_BLOCKER_CODE),
  /**
   * The vendor's own words, when there are any.
   *
   * Stored verbatim. "Phone number is not correct" is what Junction said, and paraphrasing it into
   * our vocabulary is how a practice ends up unable to find the field to fix.
   */
  detail: z.string().max(500).optional(),
  /** ISO timestamp of the attempt that failed. Shows whether a blocker is stale. */
  recordedAt: z.string().min(1),
});

export type WorkflowStepBlocker = z.infer<typeof workflowStepBlockerSchema>;

const workflowStepBlockersSchema = z.object({
  version: z.literal(1),
  blockers: z.array(workflowStepBlockerSchema).max(50),
});

/** What a patient with no recorded blockers has. */
export const NO_WORKFLOW_STEP_BLOCKERS: readonly WorkflowStepBlocker[] = [];

/**
 * Reads the stored blob, falling back to "none recorded" on anything unparseable.
 *
 * Never throws. A blocker is diagnostic decoration on a chart — a malformed blob must not take the
 * Onboarding card down with it, and "we could not read the blockers" and "there are none" lead a
 * provider to the same place: look at the step itself.
 */
export function parseWorkflowStepBlockers(raw: unknown): readonly WorkflowStepBlocker[] {
  if (typeof raw !== "string" || !raw) return NO_WORKFLOW_STEP_BLOCKERS;

  try {
    const result = workflowStepBlockersSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data.blockers : NO_WORKFLOW_STEP_BLOCKERS;
  } catch {
    return NO_WORKFLOW_STEP_BLOCKERS;
  }
}

export function serializeWorkflowStepBlockers(blockers: readonly WorkflowStepBlocker[]): string {
  return JSON.stringify({ version: 1, blockers });
}

/**
 * Replaces any blocker on the same step, rather than appending.
 *
 * One step has one current reason. Keeping the history would show a provider a rejection that was
 * fixed three attempts ago next to the one that still applies.
 */
export function upsertWorkflowStepBlocker(
  blockers: readonly WorkflowStepBlocker[],
  next: WorkflowStepBlocker,
): WorkflowStepBlocker[] {
  return [...blockers.filter((blocker) => blocker.stepId !== next.stepId), next];
}

/** Drops the step's blocker, for when the step finally goes through. */
export function clearWorkflowStepBlocker(
  blockers: readonly WorkflowStepBlocker[],
  stepId: string,
): WorkflowStepBlocker[] {
  return blockers.filter((blocker) => blocker.stepId !== stepId);
}

export function findWorkflowStepBlocker(
  blockers: readonly WorkflowStepBlocker[],
  stepId: string,
): WorkflowStepBlocker | undefined {
  return blockers.find((blocker) => blocker.stepId === stepId);
}

/** What each code means, in words a practice can act on. */
const BLOCKER_LABEL: Record<WorkflowBlockerCode, string> = {
  [WORKFLOW_BLOCKER_CODE.LabOrderRejected]: "The lab rejected this order",
  [WORKFLOW_BLOCKER_CODE.NoAuthorizingClinician]: "This lab order needs an authorizing clinician",
  [WORKFLOW_BLOCKER_CODE.PanelUnavailable]: "The configured lab panel is unavailable",
  [WORKFLOW_BLOCKER_CODE.NoBookableVisit]:
    "No visit this step offers is available to this patient's care team",
};

/**
 * The one line shown against a blocked step.
 *
 * The vendor's detail is appended rather than substituted, because the code says which of our
 * things is stuck and the detail says why. Either alone sends the practice to the wrong place.
 */
export function describeWorkflowStepBlocker(blocker: WorkflowStepBlocker): string {
  const label = BLOCKER_LABEL[blocker.code];
  return blocker.detail ? `${label}: ${blocker.detail}` : label;
}
